/**
 * Centralized retry policy for every RTK Query endpoint that flows
 * through `dynamicBaseQuery` in `src/services/api.ts`.
 *
 * ─── Design ───────────────────────────────────────────────────────────
 * Opt-out by default. Reads are retried on transient failures; any
 * mutation that is NOT provably idempotent is listed here with
 * `"NO_RETRY"` so an automatic retry can't cause a duplicate side effect.
 *
 * "Transient failure" is defined by `shouldRetryError` below:
 *   - Network errors (FETCH_ERROR / TIMEOUT_ERROR / missing status)
 *   - HTTP 5xx
 *   - HTTP 429 (rate-limited — upstream will typically recover)
 * Everything else (4xx auth/validation, 404, etc.) fails fast.
 *
 * ─── Aligning with Coral retry guidance ───────────────────────────────
 * This is a browser-side UI retry, not a Coral server-side retry.
 * But the principles translate from the Coral "Using retry strategies"
 * doc (https://docs.hub.amazon.dev/coral/user-guide/howto-retry/):
 *   - Conservative attempt cap (2 retries → 3 total attempts max).
 *   - Exponential backoff WITH JITTER to avoid thundering-herd on
 *     simultaneous reconnects (see `computeRetryDelayMs`).
 *   - Only retry transient errors on idempotent operations.
 *   - Token-bucket retry-storm protection is a server-side concept
 *     and intentionally not reproduced here; the low max-attempts
 *     cap serves a similar role client-side.
 *
 * ─── Adding a new endpoint ────────────────────────────────────────────
 * See `.claude/steering/architecture/redux-patterns.md` → "API Retry &
 * Reconnect Policy" for the decision tree. Short version: reads and
 * provably-idempotent mutations omit from the map; everything else
 * adds `"NO_RETRY"`.
 */

import type { ApiName } from "@/app/app-config";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";

/**
 * Policy override for a specific API. Only `"NO_RETRY"` is defined today
 * — kept as a string literal (not a boolean) so additional policies
 * (`"RETRY_ONCE"`, `"AGGRESSIVE"`) can be added in the future without a
 * type break for existing consumers.
 */
export type ApiRetryPolicy = "NO_RETRY";

/**
 * Conservative upper bound on retry attempts for retry-eligible calls.
 *
 * Total attempts per request = 1 original + `DEFAULT_MAX_RETRIES` retries.
 * Coral's example strategies use 2–3; we pick 2 to keep tail latency low
 * for end-user-visible calls.
 */
export const DEFAULT_MAX_RETRIES = 2;

/**
 * Per-API policy overrides. Any `ApiName` not listed here inherits the
 * default (retry on transient failures up to `DEFAULT_MAX_RETRIES`).
 *
 * Mutations listed here with `"NO_RETRY"` should stay that way until the
 * backend team confirms the operation is idempotent. For notes on specific
 * entries see inline comments + the steering doc.
 */
export const API_RETRY_POLICY: Partial<Record<ApiName, ApiRetryPolicy>> = {
  // ─── RYTE mutations ───────────────────────────────────────────────
  // Per HTTP semantics and industry consensus (Stripe / AWS / Coral
  // retry guidance), POST-creates are NOT safe to retry automatically
  // without a server-recognised idempotency key. `createYardGoal`
  // doesn't currently carry one, so an auto-retry on transient failure
  // could produce two goals from one user click. The others are state
  // transitions or full-replace updates that are "probably" idempotent,
  // but we don't retry them without explicit backend confirmation —
  // silent double-application of a state change is worse than a
  // visible transient error.
  // TODO: flip any of these to default-retry once the backend
  // confirms idempotency (either naturally idempotent by key, or
  // dedup'd via clientRequestId / Idempotency-Key header).
  CreateYardGoal: "NO_RETRY",
  UpdateYardGoal: "NO_RETRY",
  RequestCancelYardGoal: "NO_RETRY",
  RequestHoldYardGoal: "NO_RETRY",
  RequestReleaseYardGoal: "NO_RETRY",
  // YJM records recommendation history for every call. A retry after an
  // unknown network outcome would create another history entry.
  GetRecommendedEquipment: "NO_RETRY",
  // This operation attaches equipment and records the accepted/overridden
  // recommendation. Retrying an unknown outcome could repeat the attachment.
  OverrideRecommendation: "NO_RETRY",
  DeselectEquipmentForHaulAction: "NO_RETRY",

  // ─── RYC mutations ────────────────────────────────────────────────
  CreateYardJob: "NO_RETRY",
  BookVR: "NO_RETRY",
  UpdateEquipmentLocation: "NO_RETRY",
  UpdateEquipmentCoreInfo: "NO_RETRY",
  UpdateEquipmentOtherInfo: "NO_RETRY",
  // CompleteYardAlert / DismissYardAlert carry a clientRequestId (see
  // `useAlertActions`) — a hint that the backend may dedupe duplicate
  // retries. Without server-side confirmation we keep these opted out.
  // TODO: confirm dedup behaviour and flip to default if safe.
  CompleteYardAlert: "NO_RETRY",
  DismissYardAlert: "NO_RETRY",
  AddOperationalCodes: "NO_RETRY",
  RemoveOperationalCodes: "NO_RETRY",
  // Audit-add is a non-idempotent POST — request body has no client-set
  // idempotency key, so a transient retry (5xx / timeout / 429) after the
  // backend already created the equipment row would produce a duplicate.
  AuditAddEquipmentToYard: "NO_RETRY",
  AuditRemoveEquipmentFromYard: "NO_RETRY",
  // Force-checkout removes selected visits from the yard. No server-side
  // idempotency key — a transient retry after the backend already
  // checked the vehicles out would surface either a confusing "visit not
  // found" error or, worse, re-fire downstream side effects (yard events,
  // move-state transitions) the backend doesn't dedupe.
  ForceCheckOut: "NO_RETRY",
  // Emergency status override fires per-visit (the API takes one visitId
  // at a time). No client-set idempotency key — a transient retry after
  // the backend already updated the TDR state would either re-emit yard
  // events the backend doesn't dedupe, or surface a confusing
  // "TransitionNotAllowed" if the state has since moved on.
  EmergencyStatusUpdate: "NO_RETRY",

  // ─── Sesame / owner mutations ─────────────────────────────────────
  CreateEquipmentOwner: "NO_RETRY",
  UpdateEquipmentOwner: "NO_RETRY",
  CreateOrUpdateEquipmentOwners: "NO_RETRY",

  // ─── Hostler Pool mutations ──────────────────────────────────────
  // None of the three mutations carry a server-recognised idempotency
  // key today (the AddHostlerVehicle backend keys off equipmentNumber +
  // yardId, but we don't have written confirmation it dedupes a
  // millisecond-spaced retry). Same conservative stance as the other
  // mutation groups: silent double-application is worse than a visible
  // transient error the user can re-trigger.
  // TODO: flip to default-retry once the backend confirms idempotency.
  AddHostlerVehicle: "NO_RETRY",
  RemoveHostlerVehicle: "NO_RETRY",
  ForceUnpairHostler: "NO_RETRY",
};

/**
 * Resolve the max-retries cap for a given API name.
 *
 * Pure function — unit-tested without touching RTK Query. `undefined`
 * apiName (shouldn't happen in practice, but defensive) returns 0 so
 * untyped calls never retry.
 */
export const getMaxRetriesFor = (apiName: ApiName | undefined): number => {
  if (!apiName) return 0;
  return API_RETRY_POLICY[apiName] === "NO_RETRY" ? 0 : DEFAULT_MAX_RETRIES;
};

/**
 * Classify a `FetchBaseQueryError` as retry-eligible.
 *
 * Pure function — no RTK Query types leak here beyond the error shape.
 * Returns true only for transient conditions that are reasonably
 * expected to recover on retry:
 *
 *   - `"FETCH_ERROR"` — the fetch API threw (DNS, CORS, offline).
 *   - `"TIMEOUT_ERROR"` — aborted by RTK's timeout.
 *   - HTTP 5xx — upstream hiccup.
 *   - HTTP 429 — rate limited, back off and try again.
 *   - HTTP 408 — server-side request timeout (RFC 7231 §6.5.7); request
 *     did not complete on the server, so retry is safe.
 *
 * Everything else (4xx client errors, `PARSING_ERROR`,
 * `CUSTOM_ERROR`) fails fast.
 */
export const shouldRetryError = (error: FetchBaseQueryError | undefined): boolean => {
  if (!error) return false;
  const { status } = error;
  if (status === "FETCH_ERROR" || status === "TIMEOUT_ERROR") return true;
  if (typeof status === "number" && (status >= 500 || status === 429 || status === 408)) {
    return true;
  }
  return false;
};

/**
 * Compute the delay (in milliseconds) to wait before the next retry.
 *
 * Formula: `min(base * 2^(attempt-1) + jitter, cap)` where
 *   - base  = 150ms  (first retry fires quickly after a blip)
 *   - jitter = random 0..250ms (decorrelates simultaneous retries
 *              from many browsers hitting the same backend)
 *   - cap   = 4000ms (keeps worst-case wait below 4s; with 2 retries
 *              max total wait is ~4.5s which is the edge of acceptable
 *              user patience for a retry-then-success flow)
 *
 * `attempt` is 1-indexed — first retry is attempt=1, second retry
 * is attempt=2, etc. Coral's doc recommends jitter for exactly this
 * scenario; RTK Query doesn't ship jitter by default so we provide it.
 *
 * `randomFn` is injectable (defaults to `Math.random`) so tests can
 * pin the delay without relying on the PRNG.
 */
export const computeRetryDelayMs = (
  attempt: number,
  randomFn: () => number = Math.random
): number => {
  const BASE_MS = 150;
  const MAX_JITTER_MS = 250;
  const CAP_MS = 4000;
  const exponential = BASE_MS * Math.pow(2, Math.max(0, attempt - 1));
  const jitter = randomFn() * MAX_JITTER_MS;
  return Math.min(exponential + jitter, CAP_MS);
};
